import React, { useState, useEffect } from 'react';
import SharedCard   from './SharedCard';
import SharedButton from './SharedButton';
import SharedSelect from './SharedSelect';
import SharedInput  from './SharedInput';

const AttendancePanel = () => {
  const [students,          setStudents]          = useState([]);
  const [teachers,          setTeachers]          = useState([]);
  const [products,          setProducts]          = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const [selectedDate,          setSelectedDate]          = useState(new Date().toISOString().split('T')[0]);
  const [selectedSubdiscipline, setSelectedSubdiscipline] = useState('');
  const [selectedLevel,         setSelectedLevel]         = useState('');
  const [selectedTeacher,       setSelectedTeacher]       = useState('');

  const [histDate,          setHistDate]          = useState('');
  const [histSubdiscipline, setHistSubdiscipline] = useState('');
  const [histLevel,         setHistLevel]         = useState('');

  const [filteredStudents,  setFilteredStudents]  = useState([]);
  const [filteredTeachers,  setFilteredTeachers]  = useState([]);
  const [studentAttendance, setStudentAttendance] = useState({});
  const [teacherAttendance, setTeacherAttendance] = useState({});
  const [saving,            setSaving]            = useState(false);
  const [msg,               setMsg]               = useState(null);

  // ── Edición de registro existente ─────────────────────────────────────────
  const [editingRecord,         setEditingRecord]         = useState(null);
  const [editStudentAttendance, setEditStudentAttendance] = useState({});
  const [editTeacherAttendance, setEditTeacherAttendance] = useState({});

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const loadAttendance = async () => {
    try {
      const res  = await fetch('/api/list_attendance.php', { credentials: 'include' });
      const data = await res.json();
      setAttendanceRecords(Array.isArray(data) ? data : []);
    } catch { setAttendanceRecords([]); }
  };

  useEffect(() => {
    fetch('/api/list_students.php', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setStudents(Array.isArray(d) ? d.filter(s => (s.status||'').toLowerCase() === 'activo') : []))
      .catch(() => setStudents([]));

    fetch('/api/list_teachers.php', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setTeachers(Array.isArray(d) ? d : []))
      .catch(() => setTeachers([]));

    fetch('/api/list_products.php', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setProducts(Array.isArray(d) ? d : []))
      .catch(() => setProducts([]));

    loadAttendance();
  }, []);

  const allSubdisciplines = [...new Set([
    ...students.flatMap(s => (s.enrollments || []).map(e => e.subdiscipline)),
    ...products.filter(p => p.type === 'discipline').flatMap(p => p.subdisciplines || []),
    ...teachers.flatMap(t => (t.subdisciplineSchedules || []).map(sch => sch.subdiscipline)),
  ].filter(Boolean))].sort().map(s => ({ value: s, label: s }));

  const allLevels = [...new Set([
    ...students.flatMap(s =>
      (s.enrollments || [])
        .filter(e => !selectedSubdiscipline || e.subdiscipline === selectedSubdiscipline)
        .map(e => e.level)
    ),
    ...teachers.flatMap(t =>
      (t.subdisciplineSchedules || [])
        .filter(sch => !selectedSubdiscipline || sch.subdiscipline === selectedSubdiscipline)
        .map(sch => sch.level)
    ),
  ].filter(Boolean))].sort().map(l => ({ value: l, label: l }));

  const allTeachers = teachers.map(t => ({ value: t.name, label: t.name }));

  useEffect(() => {
    const stu = students.filter(s => {
      if (!selectedSubdiscipline && !selectedLevel) return true;
      return (s.enrollments || []).some(e => {
        const subMatch   = !selectedSubdiscipline || e.subdiscipline === selectedSubdiscipline;
        const levelMatch = !selectedLevel         || e.level         === selectedLevel;
        return subMatch && levelMatch;
      });
    });
    setFilteredStudents(stu);

    const tec = teachers.filter(t => {
      const subMatch  = !selectedSubdiscipline || (t.subdisciplineSchedules || []).some(sch => sch.subdiscipline === selectedSubdiscipline);
      const lvlMatch  = !selectedLevel         || (t.subdisciplineSchedules || []).some(sch => sch.level === selectedLevel);
      const nameMatch = !selectedTeacher       || t.name === selectedTeacher;
      return subMatch && lvlMatch && nameMatch;
    });
    setFilteredTeachers(tec);

    const existing = attendanceRecords.find(rec =>
      rec.date          === selectedDate &&
      rec.subdiscipline === selectedSubdiscipline &&
      rec.level         === selectedLevel &&
      rec.teacher       === selectedTeacher
    );
    if (existing) {
      setStudentAttendance(existing.studentAttendance || {});
      setTeacherAttendance(existing.teacherAttendance || {});
    } else {
      setStudentAttendance({});
      setTeacherAttendance({});
    }
  }, [selectedDate, selectedSubdiscipline, selectedLevel, selectedTeacher, students, teachers, attendanceRecords]);

  useEffect(() => { setSelectedLevel(''); }, [selectedSubdiscipline]);

  const handleStudentAttendanceChange = (studentId, isPresent) =>
    setStudentAttendance(prev => ({ ...prev, [studentId]: isPresent }));

  const handleTeacherAttendanceChange = (teacherId, isPresent) =>
    setTeacherAttendance(prev => ({ ...prev, [teacherId]: isPresent }));

  const handleSaveAttendance = async () => {
    if (!selectedDate || !selectedSubdiscipline || !selectedLevel || !selectedTeacher) {
      showMsg('error', 'Selecciona fecha, subdisciplina, nivel y maestro.');
      return;
    }
    setSaving(true);
    try {
      const res  = await fetch('/api/create_attendance.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate, subdiscipline: selectedSubdiscipline,
          level: selectedLevel, teacher: selectedTeacher,
          studentAttendance, teacherAttendance,
          recordedAt: new Date().toISOString(),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Error al guardar');
      await loadAttendance();
      showMsg('success', 'Asistencia guardada.');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Eliminar registro ──────────────────────────────────────────────────────
  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este registro de asistencia? Se eliminarán también los salarios generados.')) return;
    try {
      const res  = await fetch('/api/delete_attendance.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) { showMsg('success', 'Registro eliminado.'); await loadAttendance(); }
      else showMsg('error', data.message || 'Error al eliminar.');
    } catch { showMsg('error', 'Error de conexión.'); }
  };

  // ── Editar registro ────────────────────────────────────────────────────────
  const handleEditStart = record => {
    setEditingRecord(record);
    setEditStudentAttendance({ ...record.studentAttendance });
    setEditTeacherAttendance({ ...record.teacherAttendance });
    setTimeout(() => document.getElementById('edit-panel')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const res  = await fetch('/api/update_attendance.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingRecord.id,
          studentAttendance: editStudentAttendance,
          teacherAttendance: editTeacherAttendance,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showMsg('success', 'Registro actualizado.');
        setEditingRecord(null);
        await loadAttendance();
      } else showMsg('error', data.message || 'Error al actualizar.');
    } catch { showMsg('error', 'Error de conexión.'); }
    finally { setSaving(false); }
  };

  const filteredHistory = attendanceRecords.filter(rec => {
    if (histDate          && rec.date          !== histDate)          return false;
    if (histSubdiscipline && rec.subdiscipline !== histSubdiscipline) return false;
    if (histLevel         && rec.level         !== histLevel)         return false;
    return true;
  });

  const presentStudents = Object.values(studentAttendance).filter(Boolean).length;

  return (
    <SharedCard title="Registro de Asistencia">

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      {/* ── Panel de edición ── */}
      {editingRecord && (
        <div id="edit-panel" className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-base font-bold text-blue-900">✏️ Editando registro</h3>
              <p className="text-xs text-blue-600 mt-0.5">
                {editingRecord.date} · {editingRecord.subdiscipline} · {editingRecord.level} · {editingRecord.teacher}
              </p>
            </div>
            <button onClick={() => setEditingRecord(null)}
              className="text-blue-400 hover:text-blue-600 text-2xl leading-none">×</button>
          </div>

          {/* Estudiantes del registro */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">Estudiantes</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {Object.entries(editStudentAttendance).map(([sid, present]) => {
                const stu = students.find(s => String(s.id) === String(sid));
                const name = stu?.name || `ID ${sid}`;
                return (
                  <div key={sid} className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-gray-900">{name}</p>
                    <div className="flex gap-3">
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`edit-stu-${sid}`}
                          checked={editStudentAttendance[sid] === true}
                          onChange={() => setEditStudentAttendance(p => ({ ...p, [sid]: true }))}
                          className="accent-green-600" />
                        <span className="text-xs text-green-600 font-medium">Presente</span>
                      </label>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`edit-stu-${sid}`}
                          checked={editStudentAttendance[sid] === false}
                          onChange={() => setEditStudentAttendance(p => ({ ...p, [sid]: false }))}
                          className="accent-red-600" />
                        <span className="text-xs text-red-600 font-medium">Ausente</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Maestro del registro */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">Maestro</h4>
            <div className="space-y-2">
              {Object.entries(editTeacherAttendance).map(([tid, present]) => {
                const tch = teachers.find(t => String(t.id) === String(tid));
                const name = tch?.name || editingRecord.teacher;
                return (
                  <div key={tid} className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-gray-900">{name}</p>
                    <div className="flex gap-3">
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`edit-tch-${tid}`}
                          checked={editTeacherAttendance[tid] === true}
                          onChange={() => setEditTeacherAttendance(p => ({ ...p, [tid]: true }))}
                          className="accent-green-600" />
                        <span className="text-xs text-green-600 font-medium">Presente</span>
                      </label>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="radio" name={`edit-tch-${tid}`}
                          checked={editTeacherAttendance[tid] === false}
                          onChange={() => setEditTeacherAttendance(p => ({ ...p, [tid]: false }))}
                          className="accent-red-600" />
                        <span className="text-xs text-red-600 font-medium">Ausente</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3">
            <SharedButton onClick={handleEditSave} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </SharedButton>
            <SharedButton onClick={() => setEditingRecord(null)} className="bg-gray-500 hover:bg-gray-600">
              Cancelar
            </SharedButton>
          </div>
        </div>
      )}

      {/* Filtros de registro */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <SharedInput label="Fecha" type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
        <SharedSelect label="Subdisciplina" value={selectedSubdiscipline}
          onChange={e => setSelectedSubdiscipline(e.target.value)}
          options={[{ value: '', label: 'Selecciona subdisciplina' }, ...allSubdisciplines]} />
        <SharedSelect label="Nivel" value={selectedLevel}
          onChange={e => setSelectedLevel(e.target.value)}
          options={[{ value: '', label: 'Selecciona nivel' }, ...allLevels]} />
        <SharedSelect label="Maestro" value={selectedTeacher}
          onChange={e => setSelectedTeacher(e.target.value)}
          options={[{ value: '', label: 'Selecciona maestro' }, ...allTeachers]} />
      </div>

      {(selectedSubdiscipline || selectedLevel) && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm">
            <span className="text-blue-600 font-semibold">{filteredStudents.length}</span>
            <span className="text-blue-500 ml-1">alumnas en clase</span>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm">
            <span className="text-green-600 font-semibold">{presentStudents}</span>
            <span className="text-green-500 ml-1">presentes</span>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm">
            <span className="text-red-600 font-semibold">{filteredStudents.length - presentStudents}</span>
            <span className="text-red-500 ml-1">ausentes</span>
          </div>
        </div>
      )}

      <SharedButton onClick={handleSaveAttendance} disabled={saving} className="mb-6 bg-green-600 hover:bg-green-700">
        {saving ? 'Guardando…' : 'Guardar Asistencia'}
      </SharedButton>

      {/* Paneles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Asistencia de Estudiantes
            {filteredStudents.length > 0 && <span className="ml-2 text-xs text-gray-400 font-normal">({filteredStudents.length})</span>}
          </h3>
          {filteredStudents.length === 0 ? (
            <p className="text-gray-400 text-sm">
              {selectedSubdiscipline || selectedLevel
                ? 'No hay alumnas inscritas en esta combinación.'
                : 'Selecciona subdisciplina y nivel para ver alumnas.'}
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredStudents.map(student => (
                <div key={student.id} className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{student.name}</p>
                    <p className="text-xs text-gray-400">
                      {(student.enrollments || [])
                        .filter(e => !selectedSubdiscipline || e.subdiscipline === selectedSubdiscipline)
                        .map(e => e.level).join(', ')}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="radio" name={`student-${student.id}`}
                        checked={studentAttendance[student.id] === true}
                        onChange={() => handleStudentAttendanceChange(student.id, true)}
                        className="accent-green-600" />
                      <span className="text-xs text-green-600 font-medium">Presente</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="radio" name={`student-${student.id}`}
                        checked={studentAttendance[student.id] === false}
                        onChange={() => handleStudentAttendanceChange(student.id, false)}
                        className="accent-red-600" />
                      <span className="text-xs text-red-600 font-medium">Ausente</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Asistencia de Maestros
            {filteredTeachers.length > 0 && <span className="ml-2 text-xs text-gray-400 font-normal">({filteredTeachers.length})</span>}
          </h3>
          {filteredTeachers.length === 0 ? (
            <p className="text-gray-400 text-sm">No hay maestros para los filtros seleccionados.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredTeachers.map(teacher => (
                <div key={teacher.id} className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{teacher.name}</p>
                  <div className="flex gap-3">
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="radio" name={`teacher-${teacher.id}`}
                        checked={teacherAttendance[teacher.id] === true}
                        onChange={() => handleTeacherAttendanceChange(teacher.id, true)}
                        className="accent-green-600" />
                      <span className="text-xs text-green-600 font-medium">Presente</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="radio" name={`teacher-${teacher.id}`}
                        checked={teacherAttendance[teacher.id] === false}
                        onChange={() => handleTeacherAttendanceChange(teacher.id, false)}
                        className="accent-red-600" />
                      <span className="text-xs text-red-600 font-medium">Ausente</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Historial */}
      <div className="mt-8">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Historial de Asistencia</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <SharedInput label="Filtrar fecha" type="date" value={histDate} onChange={e => setHistDate(e.target.value)} />
          <SharedSelect label="Filtrar subdisciplina" value={histSubdiscipline}
            onChange={e => setHistSubdiscipline(e.target.value)}
            options={[{ value: '', label: 'Todas' }, ...allSubdisciplines]} />
          <SharedSelect label="Filtrar nivel" value={histLevel}
            onChange={e => setHistLevel(e.target.value)}
            options={[{ value: '', label: 'Todos' }, ...allLevels]} />
        </div>

        {filteredHistory.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">No hay registros con estos filtros.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
                  <th className="py-3 px-4 text-left">Fecha</th>
                  <th className="py-3 px-4 text-left">Subdisciplina</th>
                  <th className="py-3 px-4 text-left">Nivel</th>
                  <th className="py-3 px-4 text-left">Maestro</th>
                  <th className="py-3 px-4 text-center">Estudiantes</th>
                  <th className="py-3 px-4 text-center">Maestros</th>
                  <th className="py-3 px-4 text-left">Registrado</th>
                  <th className="py-3 px-4 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredHistory.map(record => {
                  const stuPresent = Object.values(record.studentAttendance || {}).filter(Boolean).length;
                  const stuTotal   = Object.keys(record.studentAttendance  || {}).length;
                  const tecPresent = Object.values(record.teacherAttendance || {}).filter(Boolean).length;
                  const tecTotal   = Object.keys(record.teacherAttendance  || {}).length;
                  const isEditing  = editingRecord?.id === record.id;
                  return (
                    <tr key={record.id}
                      className={`hover:bg-gray-50 transition-colors ${isEditing ? 'bg-blue-50' : ''}`}>
                      <td className="py-3 px-4 text-sm text-gray-700">{record.date}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{record.subdiscipline}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{record.level}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{record.teacher}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-sm font-semibold ${stuPresent === stuTotal && stuTotal > 0 ? 'text-green-600' : 'text-orange-500'}`}>
                          {stuPresent}/{stuTotal}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-sm font-semibold ${tecPresent === tecTotal && tecTotal > 0 ? 'text-green-600' : 'text-orange-500'}`}>
                          {tecPresent}/{tecTotal}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-400 whitespace-nowrap">
                        {new Date(record.recordedAt).toLocaleString('es-MX')}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center gap-1">
                          <button onClick={() => handleEditStart(record)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                            Editar
                          </button>
                          <button onClick={() => handleDelete(record.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SharedCard>
  );
};

export default AttendancePanel;